import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Container, PageTitle, NbButton, NbCard, Chip } from "@/components/nb";
import { Trash2, Plus, Play, Search } from "lucide-react";
import { toast } from "sonner";

export default function DoubtCorner() {
  const [doubts, setDoubts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ subject: "Math", question: "", note: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [openVideo, setOpenVideo] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/study/doubts").then((r) => setDoubts(r.data));
    api.get("/study/videos").then((r) => setVideos(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.question.trim()) return toast.error("Type a doubt");
    await api.post("/study/doubts", form);
    toast.success("Saved!");
    setForm({ subject: "Math", question: "", note: "" });
    setShowAdd(false);
    load();
  };
  const del = async (id) => { await api.delete(`/study/doubts/${id}`); load(); };

  const filteredDoubts = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return doubts;
    return doubts.filter((d) => (d.question || "").toLowerCase().includes(query) || (d.subject || "").toLowerCase().includes(query) || (d.note || "").toLowerCase().includes(query));
  }, [doubts, q]);
  const filteredVideos = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return videos;
    return videos.filter((v) => (v.title || "").toLowerCase().includes(query) || (v.subject || "").toLowerCase().includes(query));
  }, [videos, q]);

  return (
    <Container>
      <PageTitle eyebrow="DOUBT CORNER" title="Your questions. Your notes. Your library." />

      <div className="flex items-center gap-3 bg-white nb-border nb-shadow rounded-xl px-4 py-3 max-w-2xl mb-6">
        <Search className="w-5 h-5 shrink-0" />
        <input data-testid="corner-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your doubts or videos..." className="flex-1 bg-transparent outline-none py-1" />
        {q && <button onClick={() => setQ("")} data-testid="clear-corner" className="font-mono-sl text-[10px] uppercase underline">Clear</button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-black text-2xl tracking-tight">My doubts</h2>
            <NbButton onClick={() => setShowAdd((v) => !v)} color="primary" data-testid="toggle-add"><Plus className="w-4 h-4 inline-block mr-1" /> Add</NbButton>
          </div>
          {showAdd && (
            <NbCard className="mb-4" data-testid="add-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className="px-4 py-3 nb-border rounded-lg" placeholder="Subject" data-testid="doubt-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                <input className="px-4 py-3 nb-border rounded-lg" placeholder="Note (optional)" data-testid="doubt-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
              <textarea className="mt-3 w-full px-4 py-3 nb-border rounded-lg" rows={3} placeholder="Type your doubt..." data-testid="doubt-question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
              <div className="mt-3"><NbButton onClick={save} color="black" data-testid="save-doubt">Save doubt</NbButton></div>
            </NbCard>
          )}
          <div className="space-y-3" data-testid="doubt-list">
            {filteredDoubts.length === 0 && <div className="text-neutral-500">Nothing saved yet.</div>}
            {filteredDoubts.map((d) => (
              <NbCard key={d.id} className="!p-4 flex items-start gap-3">
                <Chip color="tertiary">{d.subject}</Chip>
                <div className="flex-1">
                  <div className="font-medium">{d.question}</div>
                  {d.note && <div className="text-sm text-neutral-600 mt-1">📝 {d.note}</div>}
                </div>
                <button onClick={() => del(d.id)} data-testid={`del-${d.id}`} className="p-2 rounded-lg nb-border bg-white nb-hover"><Trash2 className="w-4 h-4" /></button>
              </NbCard>
            ))}
          </div>
        </div>

        <div className="md:col-span-5">
          <h2 className="font-heading font-black text-2xl tracking-tight mb-4">3D Explainers</h2>
          <div className="grid grid-cols-1 gap-3" data-testid="video-list">
            {filteredVideos.length === 0 && <div className="text-neutral-500">No videos match your search.</div>}
            {filteredVideos.map((v) => (
              <button key={v.id} onClick={() => setOpenVideo(v)} data-testid={`video-${v.id}`}>
                <NbCard color="accent" className="!p-4 flex items-center justify-between nb-hover">
                  <div className="text-left">
                    <Chip color="tertiary">{v.subject.toUpperCase()}</Chip>
                    <div className="font-heading font-bold mt-1">{v.title}</div>
                  </div>
                  <Play className="w-6 h-6" />
                </NbCard>
              </button>
            ))}
          </div>
        </div>
      </div>

      {openVideo && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-5" onClick={() => setOpenVideo(null)} data-testid="video-modal">
          <div className="w-full max-w-3xl bg-white nb-border nb-shadow-xl rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-black flex justify-between items-center">
              <div className="font-heading font-bold">{openVideo.title}</div>
              <button onClick={() => setOpenVideo(null)} data-testid="close-video" className="font-mono-sl uppercase text-xs">Close</button>
            </div>
            <div className="aspect-video">
              <iframe title={openVideo.title} className="w-full h-full" src={`https://www.youtube.com/embed/${openVideo.youtube_id}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
